
import { Component, Emit, Prop, Watch } from 'vue-property-decorator';
import { Validations } from 'vuelidate-property-decorators';
import { maxLength } from 'vuelidate/lib/validators';
import FormSelector from '@/components/form/FormSelector.vue';
import FormInput from '@/components/form/FormInput.vue';
import FormValidationMixin from '@/mixins/FormValidationMixin';
import DateTimePicker from '@/components/form/DateTimePicker.vue';
import { getEndOfDay } from '@/lib/date-util';
import { mixins } from 'vue-class-component';
import { FormItem } from '@/model/form/FormItem';
import { Location } from '@/model/Location';
import { Topic } from '@/model/Topic';
import { Responsible } from '@/model/Responsible';
import { EventFlowItem } from '@/model/event-flow/EventFlow';
import ConfirmationModal from '../modal/ConfirmationModal.vue';

@Component({
    components: {
        FormSelector,
        FormInput,
        DateTimePicker,
        ConfirmationModal,
    },
})
export default class EventItemForm extends mixins(FormValidationMixin) {
    @Prop({ default: {} as EventFlowItem }) eventItem!: EventFlowItem;
    @Prop(Date) readonly maxDateTime!: Date;
    @Prop({ default: new Array<Topic>() }) topics!: Topic[];
    @Prop({ default: false, type: Boolean }) topicsLoading!: boolean;
    @Prop({ default: new Array<Location>() }) locations!: Location[];
    @Prop({ default: false, type: Boolean }) locationsLoading!: boolean;
    @Prop({ default: new Array<Responsible>() }) responsibles!: Responsible[];
    @Prop({ default: false, type: Boolean }) responsiblesLoading!: boolean;

    formData = {
        ...this.eventItem,
        location: this.eventItem.location ?? null,
        topic: this.eventItem.topic ?? null,
        responsible: this.eventItem.responsible ?? null,
    } as EventFlowItem;

    submitted = false;

    @Watch('formData.startedAt')
    onStartedAtChange(newVal: Date): void {
        if (newVal > this.formData.endedAt) {
            this.formData.endedAt = new Date(newVal.getTime() + 10 * 60000);
        }

        this.ensureWithinMaxDate();
    }

    @Watch('formData.endedAt')
    onEndedAtChange(): void {
        this.ensureWithinMaxDate();
    }

    @Validations()
    validations = {
        formData: {
            brief: {
                maxLength: maxLength(30),
            },
            description: {
                maxLength: maxLength(20000),
            },
            endedAt: {
                minValue(
                    val: Date,
                    { startedAt }: { startedAt: Date },
                ): boolean {
                    return val > startedAt;
                },
                maxValue: (val: Date): boolean => {
                    if (!this.effectiveMaxDateTime) {
                        return true;
                    }

                    return val <= this.effectiveMaxDateTime;
                },
            },
        },
    };

    @Emit()
    submit(): EventFlowItem {
        return this.formData;
    }

    @Emit()
    onDelete(): EventFlowItem {
        return this.formData;
    }

    onSubmit(): void {
        this.submitted = true;

        this.$v.formData.$touch();
        if (!this.$v.$invalid) {
            this.submit();
        }
    }

    get topicOptions(): FormItem[] {
        return this.topics?.map((topic) => {
            return { id: topic.id, name: topic.name, value: topic };
        });
    }

    get locationOptions(): FormItem[] {
        return this.locations?.map((location) => {
            return { id: location.id, name: location.name, value: location };
        });
    }

    get responsibleOptions(): FormItem[] {
        return this.responsibles?.map((responsible) => {
            return {
                id: responsible.id,
                name: responsible.name,
                value: responsible,
            };
        });
    }

    get maxStartDate(): Date | undefined {
        if (!this.effectiveMaxDateTime) {
            return this.formData.endedAt;
        }

        if (!this.formData.endedAt) {
            return this.effectiveMaxDateTime;
        }

        return this.formData.endedAt < this.effectiveMaxDateTime
            ? this.formData.endedAt
            : this.effectiveMaxDateTime;
    }

    get effectiveMaxDateTime(): Date | undefined {
        if (!this.maxDateTime) {
            return undefined;
        }

        if (!this.formData.startedAt) {
            return this.maxDateTime;
        }

        const endOfSelectedDay = getEndOfDay(new Date(this.formData.startedAt));

        return this.maxDateTime > endOfSelectedDay
            ? this.maxDateTime
            : endOfSelectedDay;
    }

    ensureWithinMaxDate(): void {
        if (!this.effectiveMaxDateTime) {
            return;
        }

        if (this.formData.endedAt > this.effectiveMaxDateTime) {
            this.formData.endedAt = new Date(this.effectiveMaxDateTime);
        }

        if (this.formData.startedAt >= this.formData.endedAt) {
            this.formData.endedAt = new Date(
                this.formData.startedAt.getTime() + 10 * 60000,
            );

            if (this.formData.endedAt > this.effectiveMaxDateTime) {
                this.formData.endedAt = new Date(this.effectiveMaxDateTime);
            }
        }
    }
}
